import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FriendsIcon } from './friends';
import { SettingsMenu } from './settings';
import { useMusic } from './hooks/useMusic';
import { playSoundEffect } from './hooks/useSoundEffects';
import { API_URL, WS_URL } from './config/apiConfig';
import { getWebSocketAuthMessage } from './config/apiConfig';
import { needsTokenRefresh, refreshToken, isTokenValid } from './auth';
import CustomDropdown from './CustomDropdown';
import { useContainerScale } from './hooks/useViewportScale';
import './ProvingGrounds.css';
import { BATTLE_OVERVIEW_MODE_STICKER_KEYS } from './battleOverview/modeStickerKeys';
import { useDismissBattleOverviewModeSticker } from './battleOverview/useDismissBattleOverviewModeSticker';

function ProvingGrounds({ user, onLogout }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  useDismissBattleOverviewModeSticker(user, BATTLE_OVERVIEW_MODE_STICKER_KEYS.provingGrounds);
  
  // Viewport scaling for responsive design
  const { 
    scale: viewportScale, 
    offsetX: viewportOffsetX, 
    offsetY: viewportOffsetY,
    baseWidth: containerBaseWidth,
    baseHeight: containerBaseHeight,
    isMobile: isContainerMobile
  } = useContainerScale();
  
  useMusic('/music/Epic_1.mp3');
  
  const [view, setView] = useState('main'); // 'main', 'create', 'join', 'active', 'myTournaments'
  const [tournaments, setTournaments] = useState([]);
  const [myTournaments, setMyTournaments] = useState([]);
  const [activeTournament, setActiveTournament] = useState(null);
  const [nextMatch, setNextMatch] = useState(null);
  const [participants, setParticipants] = useState([]);
  const [brawnsRankings, setBrawnsRankings] = useState([]);
  // REDESIGNED: Single source of truth for tournament status
  // Always contains the status of the ONE tournament being displayed (active takes priority, otherwise most recent completed)
  const [currentTournamentStatus, setCurrentTournamentStatus] = useState(null);
  const [showCompleteResults, setShowCompleteResults] = useState(false);
  const [allParticipantsRanked, setAllParticipantsRanked] = useState([]);
  const [strategies, setStrategies] = useState([]);
  const [selectedStrategyId, setSelectedStrategyId] = useState(null);
  const [factionTranslations, setFactionTranslations] = useState({});
  const [showQueuePopup, setShowQueuePopup] = useState(false);
  const [queueTime, setQueueTime] = useState(0);
  const [queueInterval, setQueueInterval] = useState(null);
  const [queueStatus, setQueueStatus] = useState('');
  const [tournamentQueueSocket, setTournamentQueueSocket] = useState(null);
  
  // Tournament creation form state
  const [tournamentName, setTournamentName] = useState('');
  const [maxParticipants, setMaxParticipants] = useState(32);
  const [maxGroupSize, setMaxGroupSize] = useState(4);
  const [maxGroups, setMaxGroups] = useState(8);
  const [playersPerGroupAdvance, setPlayersPerGroupAdvance] = useState(1);
  const [seededPlayersCount, setSeededPlayersCount] = useState(0);
  const [isPublic, setIsPublic] = useState(true);
  const [inviteList, setInviteList] = useState([]); // Array of { id, username }
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  
  const userId = typeof user === 'string' ? user : (user?.userId || user?.id || localStorage.getItem('userId') || user?.username);
  // Prioritize numeric ID from localStorage or user object
  const userIdFromStorage = localStorage.getItem('userId');
  const userIdPreferred = (user?.userId || user?.id || userIdFromStorage) || user?.username;
  
  // Faction colors for orbs
  const factionColors = {
    'Remnants of Humanity': '#556B2F',
    'Exolot Dominion': '#4B0082',
    'League of Shadows': '#1A1A1A',
    'Ra\'s Children': '#F4A460',
    'Asrah': '#8B0000',
    'The Sentients': '#ADD8E6',
    'Combined': '#FFA500'
  };

  // Get faction orb background color/gradient
  const getFactionOrbBackground = (factionName) => {
    if (!factionName || factionName === 'Unknown') {
      return '#ccc';
    }

    // Check if it's a combined faction and return gradient
    if (factionName === 'The Conspiracy') {
      return 'linear-gradient(to right, #8B0000 50%, #1A1A1A 50%)';
    } else if (factionName === 'The Corrupted Core') {
      return 'linear-gradient(to right, #4B0082 50%, #8B0000 50%)';
    } else if (factionName === 'The Covenant') {
      return 'linear-gradient(to right, #F4A460 50%, #1A1A1A 50%)';
    } else if (factionName === 'The Divine Syndicate') {
      return 'linear-gradient(to right, #F4A460 50%, #4B0082 50%)';
    } else if (factionName === 'The Forsaken Pact') {
      return 'linear-gradient(to right, #556B2F 50%, #8B0000 50%)';
    } else if (factionName === 'The Hollow') {
      return 'linear-gradient(to right, #F4A460 50%, #8B0000 50%)';
    } else if (factionName === 'The Iron Alliance') {
      return 'linear-gradient(to right, #556B2F 50%, #4B0082 50%)';
    } else if (factionName === 'The Last Crusade') {
      return 'linear-gradient(to right, #F4A460 50%, #556B2F 50%)';
    } else if (factionName === 'The Shadow Consortium') {
      return 'linear-gradient(to right, #1A1A1A 50%, #4B0082 50%)';
    } else if (factionName === 'The Silent Accord') {
      return 'linear-gradient(to right, #556B2F 50%, #1A1A1A 50%)';
    }

    // Return base faction color
    return factionColors[factionName] || '#ccc';
  };

  // Fetch strategies
  const fetchStrategies = useCallback(async () => {
    try {
      const token = await ensureValidToken();
      const currentLanguage = localStorage.getItem('i18nextLng') || 'en-us';
      const normalizedLanguage = currentLanguage.toLowerCase().replace('_', '-');
      const response = await fetch(`${API_URL}/my-strategies?lang=${encodeURIComponent(normalizedLanguage)}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setStrategies(data);
        
        // Auto-select first strategy if available and none selected
        setSelectedStrategyId(prev => {
          // Load from localStorage first
          const savedStrategyId = localStorage.getItem('selectedPvpStrategy') || localStorage.getItem('selectedStrategy');
          if (savedStrategyId && data.find(s => s.id.toString() === savedStrategyId)) {
            return parseInt(savedStrategyId);
          }
          // If no saved strategy, auto-select first
          if (data.length > 0 && !prev) {
            const firstId = data[0].id;
            localStorage.setItem('selectedPvpStrategy', firstId.toString());
            return firstId;
          }
          // If previously selected strategy is no longer valid, clear selection
          if (prev && !data.find(s => s.id === prev)) {
            localStorage.removeItem('selectedPvpStrategy');
            return null;
          }
          return prev;
        });
      }
    } catch (error) {
      console.error('Error fetching strategies:', error);
    }
  }, []);

  // Fetch faction translations
  useEffect(() => {
    const fetchFactionTranslations = async () => {
      try {
        const currentLanguage = localStorage.getItem('i18nextLng') || 'en-us';
        const normalizedLanguage = currentLanguage.toLowerCase().replace('_', '-');
        
        const response = await fetch(`${API_URL}/factions?lang=${encodeURIComponent(normalizedLanguage)}`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`
          }
        });
        
        if (response.ok) {
          const factions = await response.json();
          const translationMap = {};
          factions.forEach(faction => {
            translationMap[faction.english_name] = faction.name;
          });
          setFactionTranslations(translationMap);
        }
      } catch (err) {
        console.error('Failed to fetch faction translations:', err);
        setFactionTranslations({});
      }
    };
    
    fetchFactionTranslations();
  }, []);

  useEffect(() => {
    fetchTournaments();
    fetchMyTournaments();
    fetchBrawnsRankings();
    fetchStrategies();
    
    // Check for active tournament
    checkActiveTournament();
    
    // Load selected strategy from localStorage
    const savedStrategyId = localStorage.getItem('selectedPvpStrategy') || localStorage.getItem('selectedStrategy');
    if (savedStrategyId) {
      setSelectedStrategyId(parseInt(savedStrategyId));
    }
  }, [fetchStrategies]);
  
  // REDESIGNED: Determine which tournament to display and fetch its status
  // Priority: Active tournament > Most recent completed tournament
  useEffect(() => {
    if (view === 'main') {
      // Get user ID
      const userIdFromStorage = localStorage.getItem('userId');
      const userIdPreferred = (user?.userId || user?.id || userIdFromStorage) || user?.username;
      const userIdNum = userIdPreferred && !isNaN(parseInt(userIdPreferred)) ? parseInt(userIdPreferred) : null;
      
      // Find active tournament (priority 1)
      // CRITICAL: Only show tournaments where user is an ACTIVE participant
      // Don't show tournaments where user resigned or was eliminated
      const myActiveTournament = myTournaments.find(t => {
        const isActiveState = t.status !== 'completed' && t.status !== 'open';
        // Only consider 'active' participant status - exclude 'resigned', 'eliminated', 'winner', null
        const isParticipant = t.participant_status === 'active';
        // Tournament must be in active state AND user must be active participant
        // Do NOT include creators who haven't joined or have resigned
        return isActiveState && isParticipant;
      });
      
      // Find most recent completed tournament (priority 2, only if no active tournament)
      const myLastCompletedTournament = !myActiveTournament ? myTournaments
        .filter(t => t.status === 'completed')
        .sort((a, b) => {
          const dateA = new Date(a.completed_at || a.created_at || 0);
          const dateB = new Date(b.completed_at || b.created_at || 0);
          return dateB - dateA;
        })[0] : null;
      
      // Determine which tournament to display
      const tournamentToDisplay = myActiveTournament || myLastCompletedTournament;
      
      if (tournamentToDisplay) {
        console.log('[REDESIGN] Fetching status for tournament:', {
          id: tournamentToDisplay.id,
          name: tournamentToDisplay.name,
          status: tournamentToDisplay.status,
          isActive: !!myActiveTournament
        });
        // Fetch new status (don't clear first - let the new data replace it)
        // This prevents flickering where summary disappears and reappears
        fetchCurrentTournamentStatus(tournamentToDisplay.id);
      } else {
        console.log('[REDESIGN] No tournament to display, clearing status');
        setCurrentTournamentStatus(null);
      }
    }
  }, [view, myTournaments, user]);
  
  // Queue timer effect
  useEffect(() => {
    if (showQueuePopup) {
      setQueueTime(0);
      const interval = setInterval(() => setQueueTime(t => t + 1), 1000);
      setQueueInterval(interval);
      return () => clearInterval(interval);
    } else if (queueInterval) {
      clearInterval(queueInterval);
    }
  }, [showQueuePopup]);

  // REDESIGNED: Poll tournament data for the tournament being displayed (active or completed)
  useEffect(() => {
    if (view !== 'main') {
      return; // Only poll on main view
    }

    // Get user ID
    const userIdFromStorage = localStorage.getItem('userId');
    const userIdPreferred = (user?.userId || user?.id || userIdFromStorage) || user?.username;
    const userIdNum = userIdPreferred && !isNaN(parseInt(userIdPreferred)) ? parseInt(userIdPreferred) : null;

    // Find active tournament (priority 1)
    // CRITICAL: Only show tournaments where user is an ACTIVE participant
    // Don't show tournaments where user resigned or was eliminated
    const myActiveTournament = myTournaments.find(t => {
      const isActiveState = t.status !== 'completed' && t.status !== 'open';
      // Only consider 'active' participant status - exclude 'resigned', 'eliminated', 'winner', null
      const isParticipant = t.participant_status === 'active';
      // Tournament must be in active state AND user must be active participant
      return isActiveState && isParticipant;
    });

    // Find most recent completed tournament (priority 2, only if no active tournament)
    const myLastCompletedTournament = !myActiveTournament ? myTournaments
      .filter(t => t.status === 'completed')
      .sort((a, b) => {
        const dateA = new Date(a.completed_at || a.created_at || 0);
        const dateB = new Date(b.completed_at || b.created_at || 0);
        return dateB - dateA;
      })[0] : null;

    // Determine which tournament to poll
    const tournamentToPoll = myActiveTournament || myLastCompletedTournament;

    // Poll for BOTH active and completed tournaments
    if (!tournamentToPoll) {
      return; // No tournament to poll
    }

    const tournamentId = tournamentToPoll.id;

    // Poll every 10 seconds to refresh tournament data
    const pollInterval = setInterval(async () => {
      console.log('[REDESIGN Poll] Refreshing tournament data for tournament:', tournamentId, 'status:', tournamentToPoll.status);
      // Refresh my tournaments list to get latest status
      await fetchMyTournaments();
      // Refresh tournament status - this will update currentTournamentStatus
      await fetchCurrentTournamentStatus(tournamentId);
    }, 10000); // Poll every 10 seconds

    return () => {
      clearInterval(pollInterval);
    };
  }, [view, myTournaments, user]);
  
  const ensureValidToken = async () => {
    let token = localStorage.getItem('token');
    const refreshTokenValue = localStorage.getItem('refreshToken');
    
    if (token && refreshTokenValue) {
      if (!isTokenValid(token) || needsTokenRefresh(token)) {
        try {
          token = await refreshToken(refreshTokenValue);
          localStorage.setItem('token', token);
        } catch (error) {
          throw new Error('Token refresh failed. Please refresh the page.');
        }
      }
    } else if (!token) {
      throw new Error('No authentication token. Please log in again.');
    }
    
    return token;
  };
  
  const fetchTournaments = async () => {
    try {
      const token = await ensureValidToken();
      const res = await fetch(`${API_URL}/api/tournaments`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      const data = await res.json();
      if (data.error) {
        console.error('Error fetching tournaments:', data.error);
        return;
      }
      setTournaments(data);
    } catch (err) {
      console.error('Failed to fetch tournaments:', err);
    }
  };
  
  const fetchMyTournaments = async () => {
    try {
      const token = await ensureValidToken();
      const res = await fetch(`${API_URL}/api/tournaments/my-tournaments`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      const data = await res.json();
      if (data.error) {
        console.error('Error fetching my tournaments:', data.error);
        return;
      }
      setMyTournaments(data);
    } catch (err) {
      console.error('Failed to fetch my tournaments:', err);
    }
  };
  
  const fetchBrawnsRankings = async () => {
    try {
      const token = await ensureValidToken();
      const res = await fetch(`${API_URL}/honor-hall/brawns`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      const data = await res.json();
      if (data.error) {
        console.error('Error fetching brawns rankings:', data.error);
        return;
      }
      setBrawnsRankings(data);
    } catch (err) {
      console.error('Failed to fetch brawns rankings:', err);
    }
  };
  
  const checkActiveTournament = async () => {
    try {
      const token = await ensureValidToken();
      const res = await fetch(`${API_URL}/api/tournaments/my-tournaments`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      const data = await res.json();
      if (data.error) {
        return;
      }
      
      // Find active tournament - check both participant_status and if user is creator
      const userIdForCheck = typeof user === 'string' ? user : (user?.userId || user?.id || localStorage.getItem('userId') || user?.username);
      const userIdNumForCheck = userIdForCheck && !isNaN(parseInt(userIdForCheck)) ? parseInt(userIdForCheck) : null;
      const active = data.find(t => {
        const isNotCompleted = t.status !== 'completed';
        const isParticipant = t.participant_status === 'active';
        const creatorIdNum = t.creator_id ? parseInt(t.creator_id) : null;
        const isCreator = creatorIdNum && userIdNumForCheck && creatorIdNum === userIdNumForCheck;
        return isNotCompleted && (isParticipant || isCreator);
      });
      if (active) {
        setActiveTournament(active);
        await fetchTournamentDetails(active.id);
        await fetchCurrentTournamentStatus(active.id);
      }
    } catch (err) {
      console.error('Failed to check active tournament:', err);
    }
  };
  
  // REDESIGNED: Single function to fetch tournament status
  // This is the ONLY function that fetches tournament status - no confusion about which one to use
  const fetchCurrentTournamentStatus = async (tournamentId) => {
    try {
      const token = await ensureValidToken();
      const res = await fetch(`${API_URL}/api/tournaments/${tournamentId}/status`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      
      if (res.ok) {
        const data = await res.json();
        if (data && !data.error) {
          console.log('[REDESIGN] Tournament status received from backend:', {
            tournamentId,
            status: data.tournament?.status,
            hasResults: !!data.results,
            results: data.results ? 'present' : 'null'
          });
          // Simply set what the backend returns - no complex logic
          setCurrentTournamentStatus(data);
        } else {
          console.log('[REDESIGN] Tournament status response has error:', data);
          setCurrentTournamentStatus(null);
        }
      } else {
        console.error('[REDESIGN] Failed to fetch tournament status - response not ok:', res.status);
        setCurrentTournamentStatus(null);
      }
    } catch (err) {
      console.error('[REDESIGN] Failed to fetch tournament status:', err);
      setCurrentTournamentStatus(null);
    }
  };
  
  const fetchAllParticipantsRanked = async (tournamentId) => {
    try {
      const token = await ensureValidToken();
      const participantsRes = await fetch(`${API_URL}/api/tournaments/${tournamentId}/participants`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      
      if (participantsRes.ok) {
        const participantsData = await participantsRes.json();
        if (participantsData && !participantsData.error) {
          // The API already returns them sorted by status (winner, eliminated, etc.)
          setAllParticipantsRanked(participantsData);
        }
      }
    } catch (err) {
      console.error('Failed to fetch all participants:', err);
    }
  };

  const fetchTournamentDetails = async (tournamentId) => {
    try {
      const token = await ensureValidToken();
      
      // Fetch participants
      const participantsRes = await fetch(`${API_URL}/api/tournaments/${tournamentId}/participants`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      
      if (participantsRes.ok) {
        const participantsData = await participantsRes.json();
        if (participantsData && !participantsData.error) {
          setParticipants(participantsData);
        }
      }
      
      // Fetch next match
      const matchRes = await fetch(`${API_URL}/api/tournaments/${tournamentId}/next-match`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      
      if (matchRes.ok) {
        const matchData = await matchRes.json();
        if (matchData && !matchData.error) {
          setNextMatch(matchData);
        } else {
          setNextMatch(null);
        }
      } else {
        // If no match available, set to null
        setNextMatch(null);
      }
    } catch (err) {
      console.error('Failed to fetch tournament details:', err);
      // Set defaults on error
      setParticipants([]);
      setNextMatch(null);
    }
  };
  
  const addToInviteList = (user) => {
    if (!inviteList.some(u => u.id === user.id)) {
      setInviteList([...inviteList, { id: user.id, username: user.username }]);
      setSearchQuery('');
      setSearchResults([]);
    }
  };
  
  const removeFromInviteList = (userId) => {
    setInviteList(inviteList.filter(u => u.id !== userId));
  };
  
  // Debounced search for invite list
  useEffect(() => {
    if (!searchQuery || searchQuery.length < 2) {
      setSearchResults([]);
      setIsSearching(false);
      return;
    }
    
    setIsSearching(true);
    const timer = setTimeout(async () => {
      try {
        const token = await ensureValidToken();
        console.log('Searching for users with query:', searchQuery);
        const res = await fetch(`${API_URL}/friends/search`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ query: searchQuery }),
        });
        
        console.log('Search response status:', res.status);
        if (res.ok) {
          const data = await res.json();
          console.log('Search results:', data);
          // Filter out users already in invite list
          const filtered = (data.users || []).filter(user => 
            !inviteList.some(invited => invited.id === user.id)
          );
          console.log('Filtered results:', filtered);
          setSearchResults(filtered);
        } else {
          const errorData = await res.json().catch(() => ({}));
          console.error('Search failed:', res.status, errorData);
          setSearchResults([]);
        }
      } catch (err) {
        console.error('Error searching users:', err);
        setSearchResults([]);
      } finally {
        setIsSearching(false);
      }
    }, 300);
    
    return () => clearTimeout(timer);
  }, [searchQuery, inviteList]);
  
  const handleCreateTournament = async () => {
    // Clear tournament status when creating a new tournament
    setCurrentTournamentStatus(null);
    try {
      const token = await ensureValidToken();
      const res = await fetch(`${API_URL}/api/tournaments`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          name: tournamentName || null,
          maxParticipants,
          maxGroupSize,
          maxGroups,
          playersPerGroupAdvance,
          seededPlayersCount,
          isPublic,
          invitedUserIds: isPublic ? [] : inviteList.map(u => u.id), // Only send invites if invite-only
        }),
      });
      
      const data = await res.json();
      if (data.error) {
        alert(data.error);
        return;
      }
      
      // Refresh tournaments and switch to join view
      await fetchTournaments();
      await fetchMyTournaments();
      setView('join');
      alert(t('provingGrounds.tournamentCreated'));
    } catch (err) {
      console.error('Failed to create tournament:', err);
      alert(t('provingGrounds.errors.createFailed'));
    }
  };
  
  const handleJoinTournament = async (tournamentId) => {
    try {
      const token = await ensureValidToken();
      const res = await fetch(`${API_URL}/api/tournaments/${tournamentId}/join`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      
      const data = await res.json();
      if (data.error) {
        alert(data.error);
        return;
      }
      
      // Clear tournament status when joining a new tournament
      setCurrentTournamentStatus(null);
      
      await fetchTournaments();
      await fetchMyTournaments();
      alert(t('provingGrounds.tournamentJoined'));
    } catch (err) {
      console.error('Failed to join tournament:', err);
      alert(t('provingGrounds.errors.joinFailed'));
    }
  };
  
  const handleResignTournament = async (tournamentId) => {
    if (!confirm(t('provingGrounds.resignConfirm'))) {
      return;
    }
    
    try {
      const token = await ensureValidToken();
      const res = await fetch(`${API_URL}/api/tournaments/${tournamentId}/resign`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      
      const data = await res.json();
      if (data.error) {
        alert(data.error);
        return;
      }
      
      await fetchTournaments();
      await fetchMyTournaments();
      setActiveTournament(null);
      setView('main');
      alert(t('provingGrounds.tournamentResigned'));
    } catch (err) {
      console.error('Failed to resign from tournament:', err);
      alert(t('provingGrounds.errors.resignFailed'));
    }
  };
  
  const handlePrepareTournament = async (tournamentId) => {
    try {
      const token = await ensureValidToken();
      const res = await fetch(`${API_URL}/api/tournaments/${tournamentId}/prepare`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      
      const data = await res.json();
      if (data.error) {
        alert(data.error);
        return;
      }
      
      await fetchMyTournaments();
      setActiveTournament(data);
      setView('active');
      await fetchTournamentDetails(tournamentId);
      alert(t('provingGrounds.tournamentPrepared'));
    } catch (err) {
      console.error('Failed to prepare tournament:', err);
      alert(t('provingGrounds.errors.prepareFailed'));
    }
  };
  
  const handleDeleteTournament = async (tournamentId) => {
    if (!confirm(t('provingGrounds.deleteConfirm'))) {
      return;
    }
    
    try {
      const token = await ensureValidToken();
      const res = await fetch(`${API_URL}/api/tournaments/${tournamentId}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      
      const data = await res.json();
      if (data.error) {
        alert(data.error);
        return;
      }
      
      // Refresh tournaments and navigate to main view
      await fetchTournaments();
      await fetchMyTournaments();
      setActiveTournament(null);
      setCurrentTournamentStatus(null);
      setView('main');
      alert(t('provingGrounds.tournamentDeleted'));
    } catch (err) {
      console.error('Failed to delete tournament:', err);
      alert(t('provingGrounds.errors.deleteFailed'));
    }
  };
  
  const handlePlayNextGame = async (tournamentId, match) => {
    // If match is provided, use it; otherwise use nextMatch from state
    const matchToUse = match || nextMatch;
    
    if (!matchToUse) {
      alert(t('provingGrounds.noMatchAvailable'));
      return;
    }
    
    // If tournamentId is provided, use it; otherwise use activeTournament
    const tournamentToUse = tournamentId ? { id: tournamentId } : activeTournament;
    
    if (!tournamentToUse) {
      alert(t('provingGrounds.noActiveTournament'));
      return;
    }
    
    // If already in queue, don't create another connection
    if (tournamentQueueSocket) {
      return;
    }
    
    // Show queue popup immediately
    setShowQueuePopup(true);
    setQueueStatus(t('provingGrounds.searchingOpponent'));
    
    // Navigate to battle with tournament match info
    // This will be handled by WebSocket queue
    const socket = new window.WebSocket(WS_URL);
    setTournamentQueueSocket(socket);
    
    socket.onopen = () => {
      const currentLanguage = localStorage.getItem('i18nextLng') || 'en-us';
      const normalizedLanguage = currentLanguage.toLowerCase().replace('_', '-');
      const token = localStorage.getItem('token');
      socket.send(JSON.stringify(getWebSocketAuthMessage(token, normalizedLanguage)));
    };
    
    socket.onmessage = (event) => {
      const data = JSON.parse(event.data);
      console.log('Tournament queue WebSocket message:', data);
      
      if (data.type === 'auth-success') {
        // Get strategy ID from localStorage or prompt user
        const strategyId = localStorage.getItem('selectedPvpStrategy') || localStorage.getItem('selectedStrategy');
        if (!strategyId) {
          alert(t('provingGrounds.selectStrategyFirst'));
          socket.close();
          setShowQueuePopup(false);
          setTournamentQueueSocket(null);
          return;
        }
        
        socket.send(JSON.stringify({
          type: 'tournament-queue-join',
          tournamentId: tournamentToUse.id,
          strategyId: strategyId,
          matchId: matchToUse.id
        }));
      }
      
      if (data.type === 'tournament-queue-joined') {
        // Successfully joined queue, waiting for opponent
        setQueueStatus(t('provingGrounds.searchingOpponent'));
      }
      
      if (data.type === 'tournament-match-found') {
        setShowQueuePopup(false);
        socket.close();
        setTournamentQueueSocket(null);
        navigate(`/battle/${data.gameId}`, {
          state: {
            strategyId: localStorage.getItem('selectedPvpStrategy') || localStorage.getItem('selectedStrategy'),
            token: localStorage.getItem('token'),
            userId: localStorage.getItem('userId'),
            gameState: data.gameState,
            yourTurn: data.yourTurn
          }
        });
      }
      
      if (data.type === 'error') {
        alert(data.message);
        socket.close();
        setShowQueuePopup(false);
        setTournamentQueueSocket(null);
      }
    };
    
    socket.onerror = (err) => {
      console.error('WebSocket error:', err);
      socket.close();
      setShowQueuePopup(false);
      setTournamentQueueSocket(null);
    };
    
    socket.onclose = () => {
      console.log('Tournament queue WebSocket closed');
      setTournamentQueueSocket(null);
    };
  };
  
  const handleCancelTournamentQueue = () => {
    setShowQueuePopup(false);
    setQueueTime(0);
    if (tournamentQueueSocket) {
      // Get active tournament and match info
      const userIdFromStorage = localStorage.getItem('userId');
      const userIdPreferred = (user?.userId || user?.id || userIdFromStorage) || user?.username;
      const userIdNum = userIdPreferred && !isNaN(parseInt(userIdPreferred)) ? parseInt(userIdPreferred) : null;
      
      // Exclude 'open' tournaments (not started yet) and 'completed' tournaments
      const myActiveTournament = myTournaments.find(t => {
        const isActiveState = t.status !== 'completed' && t.status !== 'open';
        const isParticipant = t.participant_status === 'active';
        const creatorIdNum = t.creator_id ? parseInt(t.creator_id) : null;
        const isCreator = creatorIdNum && userIdNum && creatorIdNum === userIdNum;
        // CRITICAL: Only show tournaments where user is an ACTIVE participant
        // Don't show tournaments where user resigned or was eliminated
        return isActiveState && isParticipant;
      });
      
      if (myActiveTournament && nextMatch) {
        tournamentQueueSocket.send(JSON.stringify({
          type: 'tournament-queue-leave',
          tournamentId: myActiveTournament.id,
          matchId: nextMatch.id
        }));
      }
      
      tournamentQueueSocket.close();
      setTournamentQueueSocket(null);
    }
  };
  
  const handleStartPendingBattle = async () => {
    // Get active tournament
    const userIdFromStorage = localStorage.getItem('userId');
    const userIdPreferred = (user?.userId || user?.id || userIdFromStorage) || user?.username;
    const userIdNum = userIdPreferred && !isNaN(parseInt(userIdPreferred)) ? parseInt(userIdPreferred) : null;
    
    // Exclude 'open' tournaments (not started yet) and 'completed' tournaments
    const myActiveTournament = myTournaments.find(t => {
      const isActiveState = t.status !== 'completed' && t.status !== 'open';
      const isParticipant = t.participant_status === 'active';
      const creatorIdNum = t.creator_id ? parseInt(t.creator_id) : null;
      const isCreator = creatorIdNum && userIdNum && creatorIdNum === userIdNum;
      return isActiveState && (isParticipant || isCreator);
    });
    
    if (!myActiveTournament) {
      alert(t('provingGrounds.noActiveTournament'));
      return;
    }
    
    if (myActiveTournament.status !== 'active') {
      alert(t('provingGrounds.tournamentNotActive'));
      return;
    }
    
    try {
      // Fetch next match for this tournament
      const token = await ensureValidToken();
      const matchRes = await fetch(`${API_URL}/api/tournaments/${myActiveTournament.id}/next-match`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      
      if (matchRes.ok) {
        const matchData = await matchRes.json();
        // Handle null response (no match available) vs error response
        if (matchData === null) {
          alert(t('provingGrounds.noMatchAvailable'));
          return;
        }
        if (matchData && !matchData.error) {
          setNextMatch(matchData);
          await handlePlayNextGame(myActiveTournament.id, matchData);
        } else {
          alert(matchData?.error || t('provingGrounds.noMatchAvailable'));
        }
      } else {
        const errorData = await matchRes.json().catch(() => ({}));
        alert(errorData?.error || t('provingGrounds.noMatchAvailable'));
      }
    } catch (err) {
      console.error('Failed to fetch next match:', err);
      alert(t('provingGrounds.errors.fetchMatchFailed'));
    }
  };
  
  const renderMainView = () => {
    // Get user ID - prioritize numeric ID from localStorage or user object
    const userIdFromStorage = localStorage.getItem('userId');
    const userIdPreferred = (user?.userId || user?.id || userIdFromStorage) || user?.username;
    const userIdNum = userIdPreferred && !isNaN(parseInt(userIdPreferred)) ? parseInt(userIdPreferred) : null;
    
    // Find active tournament (not completed)
    // Only consider tournaments that are not completed AND where user has active participant status
    // Exclude tournaments where participant status is 'eliminated', 'resigned', or 'winner' (for completed tournaments)
    // Also exclude tournaments with status 'open' (not started yet) - these shouldn't block joining
    const myActiveTournament = myTournaments.find(t => {
      // Tournament must be in a state where it's actually active (not 'open' or 'completed')
      const isActiveState = t.status !== 'completed' && t.status !== 'open';
      // Only consider 'active' participant status - exclude 'eliminated', 'resigned', 'winner', null
      const isParticipant = t.participant_status === 'active';
      // Tournament must be in active state AND user must be active participant
      return isActiveState && isParticipant;
    });
    
    // Find most recent completed tournament (to show results)
    // If a tournament is in myTournaments, the user either created it or participated in it
    // So we can show all completed tournaments from myTournaments
    const myLastCompletedTournament = myTournaments
      .filter(t => {
        // Tournament must be completed
        return t.status === 'completed';
      })
      .sort((a, b) => {
        // Sort by completed_at or created_at descending (most recent first)
        const dateA = new Date(a.completed_at || a.created_at || 0);
        const dateB = new Date(b.completed_at || b.created_at || 0);
        return dateB - dateA;
      })[0]; // Get the most recent one
    
    console.log('renderMainView:', {
      myTournamentsCount: myTournaments.length,
      myTournaments: myTournaments.map(t => ({ id: t.id, status: t.status, participant_status: t.participant_status, creator_id: t.creator_id })),
      userIdPreferred,
      userIdNum,
      userIdFromStorage,
      myActiveTournament: myActiveTournament ? { id: myActiveTournament.id, status: myActiveTournament.status, participant_status: myActiveTournament.participant_status, creator_id: myActiveTournament.creator_id } : null,
      currentTournamentStatus: currentTournamentStatus ? { 
        status: currentTournamentStatus.tournament?.status, 
        hasGroups: !!currentTournamentStatus.groups, 
        groupsCount: currentTournamentStatus.groups ? Object.keys(currentTournamentStatus.groups).length : 0,
        hasResults: !!currentTournamentStatus.results
      } : null,
      user: user ? { id: user.id, userId: user.userId, username: user.username, type: typeof user } : null
    });
    
    return (
      <div className="provinggrounds-main">
        <div className="provinggrounds-header">
          <h2>{t('provingGrounds.title')}</h2>
          
          {/* Strategy Selector - Upper Right Corner */}
          <div className="strategy-selector-container">
            <label className="strategy-selector-label">
              {t('provingGrounds.selectStrategy')}
            </label>
            {strategies.length === 0 ? (
              <div className="strategy-selector-empty">
                {t('provingGrounds.noStrategiesAvailable')}
              </div>
            ) : (
              <div className="strategy-selector-dropdown-wrapper">
                <CustomDropdown
                  options={strategies.map(strategy => ({
                    value: strategy.id.toString(),
                    label: strategy.name,
                    faction_name: strategy.faction_name
                  }))}
                  value={selectedStrategyId ? selectedStrategyId.toString() : ''}
                  onChange={(strategyId) => {
                    const id = strategyId ? parseInt(strategyId) : null;
                    setSelectedStrategyId(id);
                    if (id) {
                      localStorage.setItem('selectedPvpStrategy', id.toString());
                    } else {
                      localStorage.removeItem('selectedPvpStrategy');
                    }
                  }}
                  placeholder={t('provingGrounds.selectStrategyPlaceholder')}
                  openUpward={true}
                  renderOption={(option) => {
                    const strategy = strategies.find(s => s.id.toString() === option.value);
                    const factionName = strategy?.faction_name || option.faction_name;
                    if (!factionName) {
                      return <span>{option.label}</span>;
                    }
                    const translatedFactionName = strategy?.faction_name_translated || factionTranslations[factionName] || factionName;
                    return (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <div
                          className="faction-orb"
                          style={{ background: getFactionOrbBackground(factionName) }}
                          title={translatedFactionName}
                        />
                        <span>{option.label}</span>
                      </div>
                    );
                  }}
                />
              </div>
            )}
          </div>
        </div>
        <div className="provinggrounds-actions">
          <button 
            className="provinggrounds-button"
            onClick={() => {
              playSoundEffect('/sounds/click.mp3');
              // Clear form data when starting new tournament creation
              setTournamentName('');
              setInviteList([]);
              setSearchQuery('');
              setSearchResults([]);
              setView('create');
            }}
          >
            {t('provingGrounds.createTournament')}
          </button>
          <button 
            className="provinggrounds-button"
            onClick={() => {
              playSoundEffect('/sounds/click.mp3');
              setView('join');
              fetchTournaments();
            }}
          >
            {t('provingGrounds.joinTournament')}
          </button>
          <button 
            className="provinggrounds-button"
            onClick={async () => {
              playSoundEffect('/sounds/click.mp3');
              await fetchMyTournaments();
              setView('myTournaments');
            }}
          >
            {t('provingGrounds.myTournaments')}
          </button>
          {/* Only show "Start Pending Battle" if tournament is actually active (not completed) */}
          {currentTournamentStatus && currentTournamentStatus.tournament?.status === 'active' && (
            <button 
              className="provinggrounds-button"
              onClick={async () => {
                playSoundEffect('/sounds/click.mp3');
                await handleStartPendingBattle();
              }}
              disabled={!!tournamentQueueSocket}
            >
              {t('provingGrounds.startPendingBattle')}
            </button>
          )}
        </div>
        
        {/* REDESIGNED: Simple check - if we have tournament status data, display it */}
        {currentTournamentStatus && (
          <div className="tournament-status-main">
            <h3>{currentTournamentStatus.tournament?.name || 
                 (myActiveTournament || myLastCompletedTournament)?.name || 
                 `Tournament #${currentTournamentStatus.tournament?.id || (myActiveTournament || myLastCompletedTournament)?.id}`}</h3>
            {/* Show groups if tournament is not completed and group phase is not complete */}
            {/* REDESIGNED: Simple check - just use currentTournamentStatus */}
            {currentTournamentStatus.tournament?.status !== 'completed' && 
             !currentTournamentStatus.groupPhaseComplete ? (
              // Show groups with standings
              <div className="tournament-groups">
                <h4>{t('provingGrounds.groupStage')}</h4>
                {Object.keys(currentTournamentStatus.groups || {}).map(groupId => (
                  <div key={groupId} className="tournament-group">
                    <h5>{t('provingGrounds.group')} {String.fromCharCode(64 + parseInt(groupId))}</h5>
                    <table className="group-standings">
                      <thead>
                        <tr>
                          <th>{t('provingGrounds.position')}</th>
                          <th>{t('provingGrounds.player')}</th>
                          <th>{t('provingGrounds.wins')}</th>
                          <th>{t('provingGrounds.losses')}</th>
                          <th>{t('provingGrounds.matches')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {currentTournamentStatus.groups[groupId]?.map((player, index) => (
                          <tr key={player.userId} className={player.userId === (user?.userId || user?.id) ? 'current-player' : ''}>
                            <td>{index + 1}</td>
                            <td>
                              {player.username}
                              {player.isSeeded && <span className="seed-badge"> #{player.seedRank}</span>}
                            </td>
                            <td>{player.wins}</td>
                            <td>{player.losses}</td>
                            <td>{player.matches}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ))}
              </div>
            ) : (
              // Show bracket tree (for completed tournaments or when group phase is complete)
              <div className="tournament-bracket">
                {/* REDESIGNED: Simple check - if backend says tournament is completed and has results, show them */}
                {(() => {
                  const isCompleted = currentTournamentStatus?.tournament?.status === 'completed';
                  const hasResults = !!currentTournamentStatus?.results;
                  const shouldShow = isCompleted && hasResults;
                  
                  if (currentTournamentStatus) {
                    console.log('[RENDER] Tournament results check:', {
                      tournamentId: currentTournamentStatus.tournament?.id,
                      status: currentTournamentStatus.tournament?.status,
                      isCompleted,
                      hasResults,
                      results: currentTournamentStatus.results ? 'present' : 'null',
                      shouldShow
                    });
                  }
                  
                  return shouldShow;
                })() && (
                  <div className="tournament-results">
                    <h3 className="tournament-completed-title">
                      {currentTournamentStatus.tournament?.name || 
                       (myActiveTournament || myLastCompletedTournament)?.name || 
                       `Tournament #${currentTournamentStatus.tournament?.id || (myActiveTournament || myLastCompletedTournament)?.id}`} {t('provingGrounds.isOver')}
                    </h3>
                    <div className="tournament-results-list">
                      {currentTournamentStatus.results?.winner && (
                        <div className="tournament-result-item winner">
                          <span className="result-label">{t('provingGrounds.winner')}</span>
                          <span className="result-player">{currentTournamentStatus.results.winner.username}</span>
                        </div>
                      )}
                      {currentTournamentStatus.results?.secondPlace && (
                        <div className="tournament-result-item second-place">
                          <span className="result-label">{t('provingGrounds.secondPlace')}</span>
                          <span className="result-player">{currentTournamentStatus.results.secondPlace.username}</span>
                        </div>
                      )}
                      {currentTournamentStatus.results?.thirdPlace && (
                        <div className="tournament-result-item third-place">
                          <span className="result-label">{t('provingGrounds.thirdPlace')}:</span>
                          <span className="result-player">{currentTournamentStatus.results.thirdPlace.username}</span>
                        </div>
                      )}
                    </div>
                    <button
                      className="provinggrounds-button"
                      onClick={async () => {
                        playSoundEffect('/sounds/click.mp3');
                        const tournamentId = currentTournamentStatus.tournament?.id;
                        if (tournamentId) {
                          await fetchAllParticipantsRanked(tournamentId);
                          setShowCompleteResults(true);
                        }
                      }}
                      style={{ marginTop: '15px' }}
                    >
                      {t('provingGrounds.seeCompleteResults')}
                    </button>
                  </div>
                )}
                <h4>{t('provingGrounds.knockoutStage')}</h4>
                <div className="bracket-tree">
                  {currentTournamentStatus.knockoutMatches && currentTournamentStatus.knockoutMatches.length > 0 ? (
                    <div className="bracket-matches">
                      {currentTournamentStatus.knockoutMatches.map((match, index) => {
                        console.log('Rendering bracket match:', match);
                        const player1Username = match.player1_username || `Player ${match.player1_id}`;
                        const player2Username = match.player2_username || `Player ${match.player2_id}`;
                        const winnerUsername = match.winner_id ? 
                          (parseInt(match.winner_id) === parseInt(match.player1_id) ? player1Username : player2Username) : null;
                        const userIdNum = user?.userId || user?.id || (user ? parseInt(localStorage.getItem('userId')) : null);
                        const isPlayer1Match = parseInt(match.player1_id) === userIdNum || parseInt(match.player2_id) === userIdNum;
                        
                        return (
                          <div key={match.id || index} className={`bracket-match ${match.status} ${isPlayer1Match ? 'player-match' : ''}`}>
                            <div className="bracket-match-round">
                              {match.round_type === 'third_place' 
                                ? t('provingGrounds.thirdPlace')
                                : match.round_type.replace('_', ' ').toUpperCase()}
                            </div>
                            <div className="bracket-match-players">
                              <div className={`bracket-player ${match.winner_id === match.player1_id ? 'winner' : ''} ${match.player1_id === user?.userId ? 'current-player' : ''}`}>
                                {player1Username}
                              </div>
                              <div className="bracket-vs">vs</div>
                              <div className={`bracket-player ${match.winner_id === match.player2_id ? 'winner' : ''} ${match.player2_id === user?.userId ? 'current-player' : ''}`}>
                                {player2Username}
                              </div>
                            </div>
                            {match.status === 'completed' && winnerUsername && (
                              <div className="bracket-winner">
                                {match.round_type === 'third_place' 
                                  ? `${t('provingGrounds.thirdPlace')}: ${winnerUsername}`
                                  : `${t('provingGrounds.winner')} ${winnerUsername}`}
                              </div>
                            )}
                            {match.status === 'pending' && (
                              <div className="bracket-status">Pending</div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <p>{t('provingGrounds.bracketStarting')}</p>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };
  
  const renderCreateView = () => (
    <div className="provinggrounds-create">
      <h2>{t('provingGrounds.createTournament')}</h2>
      <div className="tournament-form">
        <div className="form-group">
          <label>{t('provingGrounds.tournamentName')}</label>
          <input
            type="text"
            value={tournamentName}
            onChange={(e) => setTournamentName(e.target.value)}
            placeholder={t('provingGrounds.tournamentNamePlaceholder')}
          />
        </div>
        
        <div className="form-group">
          <label>{t('provingGrounds.maxParticipants')}</label>
          <input
            type="number"
            min="2"
            max="128"
            value={maxParticipants}
            onChange={(e) => setMaxParticipants(parseInt(e.target.value) || 2)}
          />
        </div>
        
        <div className="form-group">
          <label>{t('provingGrounds.maxGroupSize')}</label>
          <input
            type="number"
            min="2"
            max="16"
            value={maxGroupSize}
            onChange={(e) => setMaxGroupSize(parseInt(e.target.value) || 2)}
          />
        </div>
        
        <div className="form-group">
          <label>{t('provingGrounds.maxGroups')}</label>
          <select
            value={maxGroups}
            onChange={(e) => setMaxGroups(parseInt(e.target.value))}
          >
            <option value={32}>32</option>
            <option value={16}>16</option>
            <option value={8}>8</option>
            <option value={4}>4</option>
            <option value={2}>2</option>
          </select>
        </div>
        
        <div className="form-group">
          <label>{t('provingGrounds.playersPerGroupAdvance')}</label>
          <input
            type="number"
            min="1"
            max={maxGroupSize}
            value={playersPerGroupAdvance}
            onChange={(e) => setPlayersPerGroupAdvance(parseInt(e.target.value) || 1)}
          />
        </div>
        
        <div className="form-group">
          <label>{t('provingGrounds.seededPlayersCount')}</label>
          <input
            type="number"
            min="0"
            max={brawnsRankings.length}
            value={seededPlayersCount}
            onChange={(e) => setSeededPlayersCount(parseInt(e.target.value) || 0)}
          />
          <small>{t('provingGrounds.seededPlayersHint', { count: seededPlayersCount })}</small>
        </div>
        
        <div className="form-group">
          <label>{t('provingGrounds.tournamentType')}</label>
          <div className="radio-group">
            <label>
              <input
                type="radio"
                checked={isPublic}
                onChange={() => {
                  setIsPublic(true);
                  setInviteList([]); // Clear invites when switching to public
                }}
              />
              {t('provingGrounds.public')}
            </label>
            <label>
              <input
                type="radio"
                checked={!isPublic}
                onChange={() => setIsPublic(false)}
              />
              {t('provingGrounds.inviteOnly')}
            </label>
          </div>
        </div>
        
        {!isPublic && (
          <div className="form-group">
            <label>{t('provingGrounds.invitePlayers')}</label>
            <div className="invite-search">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={t('provingGrounds.searchPlayers')}
                className="invite-search-input"
              />
              {isSearching && <span className="search-loading">{t('provingGrounds.searching')}</span>}
              {searchResults.length > 0 && (
                <div className="search-results">
                  {searchResults.map(user => (
                    <div key={user.id} className="search-result-item">
                      <span>{user.username}</span>
                      <button
                        type="button"
                        className="add-invite-button"
                        onClick={() => addToInviteList(user)}
                      >
                        {t('provingGrounds.add')}
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            {inviteList.length > 0 && (
              <div className="invite-list">
                <h4>{t('provingGrounds.invitedPlayers')} ({inviteList.length})</h4>
                <ul className="invite-list-items">
                  {inviteList.map(user => (
                    <li key={user.id} className="invite-list-item">
                      <span>{user.username}</span>
                      <button
                        type="button"
                        className="remove-invite-button"
                        onClick={() => removeFromInviteList(user.id)}
                      >
                        {t('provingGrounds.remove')}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
        
        <button 
          type="button"
          className="provinggrounds-button"
          onClick={() => {
            playSoundEffect('/sounds/click.mp3');
            handleCreateTournament();
          }}
        >
          {t('provingGrounds.create')}
        </button>
        
        <button 
          type="button"
          className="provinggrounds-button-secondary"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            playSoundEffect('/sounds/click.mp3');
            // Clear form data when canceling
            setTournamentName('');
            setInviteList([]);
            setSearchQuery('');
            setSearchResults([]);
            setView('main');
          }}
        >
          {t('common.cancel')}
        </button>
      </div>
    </div>
  );
  
  const renderJoinView = () => {
    // Find active tournament - check both participant_status and if user is creator
    const userIdForCheck = typeof user === 'string' ? user : (user?.userId || user?.id || localStorage.getItem('userId') || user?.username);
    const userIdNumForCheck = userIdForCheck && !isNaN(parseInt(userIdForCheck)) ? parseInt(userIdForCheck) : null;
    
    // Log all tournaments for debugging
    console.log('[renderJoinView] All myTournaments:', myTournaments.map(t => ({
      id: t.id,
      name: t.name,
      status: t.status,
      participant_status: t.participant_status,
      creator_id: t.creator_id
    })));
    
    const myActiveTournament = myTournaments.find(t => {
      // Tournament must be in a state where it's actually active (not 'open' or 'completed')
      // 'open' tournaments haven't started yet, so they shouldn't block joining
      const isActiveState = t.status !== 'completed' && t.status !== 'open';
      // User must have active participant status (exclude null, 'eliminated', 'resigned', 'winner')
      // IMPORTANT: Only check participant_status, NOT creator status
      // This ensures creators who haven't joined can still join other tournaments
      const isActiveParticipant = t.participant_status === 'active';
      // Tournament must be in active state AND user must be active participant
      // Do NOT include creators - they can join other tournaments if they haven't joined their own
      const result = isActiveState && isActiveParticipant;
      
      // Debug log for all tournaments that are not completed
      if (t.status !== 'completed' && t.status !== 'open') {
        console.log('[renderJoinView] Tournament check:', {
          id: t.id,
          name: t.name,
          status: t.status,
          participant_status: t.participant_status,
          creator_id: t.creator_id,
          isActiveState,
          isActiveParticipant,
          result
        });
      }
      
      return result;
    });
    
    console.log('[renderJoinView] Final myActiveTournament:', myActiveTournament ? {
      id: myActiveTournament.id,
      name: myActiveTournament.name,
      status: myActiveTournament.status,
      participant_status: myActiveTournament.participant_status
    } : null);
    
    return (
      <div className="provinggrounds-join">
        <h2>{t('provingGrounds.joinTournament')}</h2>
        
        {myActiveTournament && (
          <div className="active-tournament-warning">
            <div className="tournament-info">
              <p>{t('provingGrounds.youAreIn')} {myActiveTournament.name || `Tournament #${myActiveTournament.id}`}</p>
            </div>
            <button
              type="button"
              className="provinggrounds-button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                playSoundEffect('/sounds/click.mp3');
                if (myActiveTournament.status === 'active') {
                  setActiveTournament(myActiveTournament);
                  setView('active');
                  fetchTournamentDetails(myActiveTournament.id);
                } else {
                  handleResignTournament(myActiveTournament.id);
                }
              }}
              style={{ minWidth: '120px' }}
            >
              {myActiveTournament.status === 'active' ? t('provingGrounds.viewTournament') : t('provingGrounds.resign')}
            </button>
          </div>
        )}
        
        <div className="tournament-list">
          <h3>{t('provingGrounds.publicTournaments')}</h3>
          {tournaments.filter(t => t.status === 'open').map(tournament => {
            // Check if user has joined this tournament
            // Use is_participant from API response, or fallback to comparing with myActiveTournament
            const isJoined = tournament.is_participant === true || 
                           (myActiveTournament && parseInt(myActiveTournament.id) === parseInt(tournament.id));
            
            return (
              <div key={tournament.id} className="tournament-item">
                <div className="tournament-info">
                  <h4>{tournament.name || `Tournament #${tournament.id}`}</h4>
                  <p>{t('provingGrounds.participants')} {tournament.participant_count}/{tournament.max_participants}</p>
                  <p>{t('provingGrounds.createdBy')} {tournament.creator_username}</p>
                </div>
                {isJoined ? (
                  <button
                    type="button"
                    className="provinggrounds-button"
                    onClick={async (e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      playSoundEffect('/sounds/click.mp3');
                      await handleResignTournament(tournament.id);
                      // Refresh tournaments list after leaving
                      await fetchTournaments();
                      // Refresh my tournaments to update active tournament
                      await fetchMyTournaments();
                    }}
                    style={{ minWidth: '120px' }}
                  >
                    {t('provingGrounds.leave')}
                  </button>
                ) : (
                  <button
                    type="button"
                    className="provinggrounds-button"
                    onClick={async (e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      playSoundEffect('/sounds/click.mp3');
                      await handleJoinTournament(tournament.id);
                      // Refresh tournaments list after joining
                      await fetchTournaments();
                      // Refresh my tournaments to update active tournament
                      await fetchMyTournaments();
                    }}
                    disabled={
                      // Allow joining 'open' tournaments even if you have an active tournament
                      // (because 'open' tournaments haven't started yet)
                      // But block joining 'active' or 'pending' tournaments if you're already in one
                      (!!myActiveTournament && tournament.status !== 'open') || 
                      parseInt(tournament.participant_count) >= tournament.max_participants
                    }
                    style={{ minWidth: '120px' }}
                  >
                    {t('provingGrounds.join')}
                  </button>
                )}
              </div>
            );
          })}
        </div>
        
        <button 
          type="button"
          className="provinggrounds-button-secondary"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            playSoundEffect('/sounds/click.mp3');
            setView('main');
          }}
        >
          {t('common.back')}
        </button>
      </div>
    );
  };
  
  const renderMyTournamentsView = () => {
    // Show all tournaments returned from server
    // The server already filters to show tournaments created by the user
    const tournamentsToShow = myTournaments;
    
    return (
      <div className="provinggrounds-join">
        <h2>{t('provingGrounds.myTournaments')}</h2>
        
        {tournamentsToShow.length === 0 ? (
          <div>
            <p>{t('provingGrounds.noTournamentsCreated')}</p>
          </div>
        ) : (
          <div className="tournament-list">
            {tournamentsToShow.map(tournament => (
              <div key={tournament.id} className="tournament-item">
                <div className="tournament-info">
                  <h4>{tournament.name || `Tournament #${tournament.id}`}</h4>
                  <p>{t('provingGrounds.participants')} {tournament.participant_count || 0}/{tournament.max_participants}</p>
                  <p>{t('provingGrounds.status')}: {tournament.status}</p>
                </div>
                <div style={{ display: 'flex', gap: '10px', flexDirection: 'column' }}>
                  <button
                    type="button"
                    className="provinggrounds-button"
                    onClick={async (e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      playSoundEffect('/sounds/click.mp3');
                      setActiveTournament(tournament);
                      await fetchTournamentDetails(tournament.id);
                      setView('active');
                    }}
                  >
                    {t('provingGrounds.viewTournament')}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
        
        <button 
          type="button"
          className="provinggrounds-button-secondary"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            playSoundEffect('/sounds/click.mp3');
            setView('main');
          }}
        >
          {t('common.back')}
        </button>
      </div>
    );
  };
  
  const renderActiveView = () => {
    if (!activeTournament) {
      return <div>{t('provingGrounds.noMatchesAvailable')}</div>;
    }
    
    const isCreator = activeTournament.creator_id === parseInt(localStorage.getItem('userId'));
    
    const userIdNum = user?.userId || user?.id || (user ? parseInt(localStorage.getItem('userId')) : null);
    const isParticipant = activeTournament.status === 'active' && participants.some(p => p.user_id === userIdNum);
    
    return (
      <div className="provinggrounds-active" style={{ position: 'relative' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h2 style={{ margin: 0 }}>{activeTournament.name || `Tournament #${activeTournament.id}`}</h2>
          {isCreator && (
            <button
              type="button"
              className="provinggrounds-button"
              style={{ backgroundColor: '#8B0000', borderColor: '#FF0000' }}
              onClick={async (e) => {
                e.preventDefault();
                e.stopPropagation();
                playSoundEffect('/sounds/click.mp3');
                await handleDeleteTournament(activeTournament.id);
              }}
            >
              {t('provingGrounds.deleteTournament')}
            </button>
          )}
        </div>
        
        {activeTournament.status === 'open' && isCreator && (
          <div className="tournament-prepare">
            <p>{t('provingGrounds.participants')} {participants.length}/{activeTournament.max_participants}</p>
            <button
              className="provinggrounds-button"
              onClick={() => {
                playSoundEffect('/sounds/click.mp3');
                handlePrepareTournament(activeTournament.id);
              }}
              disabled={participants.length < 2}
            >
              {t('provingGrounds.prepareTournament')}
            </button>
          </div>
        )}
        
        
        <div className="tournament-participants">
          <h3>{t('provingGrounds.participantsList')}</h3>
          <ul>
            {participants.map(p => (
              <li key={p.id}>{p.username}</li>
            ))}
          </ul>
        </div>
        
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '20px' }}>
          <button
            className="provinggrounds-button-secondary"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              playSoundEffect('/sounds/click.mp3');
              setView('main');
            }}
          >
            {t('common.back')}
          </button>
          
          {activeTournament.status === 'active' && isParticipant && (
            <button
              type="button"
              className="provinggrounds-button-secondary"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                playSoundEffect('/sounds/click.mp3');
                if (confirm(t('provingGrounds.resignConfirm'))) {
                  handleResignTournament(activeTournament.id);
                }
              }}
            >
              {t('provingGrounds.resign')}
            </button>
          )}
        </div>
      </div>
    );
  };
  
  return (
    <div className="provinggrounds-viewport-wrapper">
      {/* Full-screen background - outside viewport-container to cover entire screen */}
      <div 
        className="provinggrounds-fullscreen-background"
        style={{ backgroundImage: 'url(/images/tournament.jpg)' }}
      />
      
      {/* Viewport container - 1920x1080 space that scales to fit screen */}
      <div 
        className={`provinggrounds-viewport-container${isContainerMobile ? ' mobile' : ''}`}
        style={{
          width: `${containerBaseWidth}px`,
          height: `${containerBaseHeight}px`,
          transformOrigin: '0 0',
          transform: `translate(${viewportOffsetX}px, ${viewportOffsetY}px) scale(${viewportScale})`,
        }}
      >
        <div className="provinggrounds-container">
          <div style={{ position: 'absolute', top: 10, left: 10, zIndex: 1000, display: 'flex', alignItems: 'center' }}>
            <FriendsIcon />
            <SettingsMenu onLogout={() => {
              playSoundEffect('/sounds/click.mp3');
              onLogout();
              navigate('/');
            }} />
            <span style={{ color: 'white', marginRight: '10px' }}>{typeof user === 'string' ? user : (user?.username ?? '')}</span>
          </div>

      <button onClick={() => {
        playSoundEffect('/sounds/click.mp3');
        navigate('/battleoverview');
      }} className="back-button">
        {t('common.back')}
      </button>

      {view === 'main' && renderMainView()}
      {view === 'create' && renderCreateView()}
      {view === 'join' && renderJoinView()}
      {view === 'myTournaments' && renderMyTournamentsView()}
      {view === 'active' && renderActiveView()}
      
      {showQueuePopup && (
        <div className="queue-popup">
          <div className="queue-popup-content">
            <h3>{queueStatus}</h3>
            <p>{t('provingGrounds.timeInQueue')} {queueTime} {t('provingGrounds.seconds')}</p>
            <button onClick={() => {
              playSoundEffect('/sounds/click.mp3');
              handleCancelTournamentQueue();
            }}>{t('common.cancel')}</button>
          </div>
        </div>
      )}

      {showCompleteResults && (
        <div className="complete-results-popup">
          <div className="complete-results-content">
            <h3>{t('provingGrounds.completeResults')}</h3>
            <div className="complete-results-list">
              {allParticipantsRanked.map((participant, index) => {
                const rank = index + 1;
                let rankClass = '';
                let rankLabel = `#${rank}`;
                
                if (participant.status === 'winner') {
                  rankClass = 'winner';
                  rankLabel = t('provingGrounds.winner');
                } else if (rank === 2 && participant.status !== 'winner') {
                  rankClass = 'second-place';
                  rankLabel = t('provingGrounds.secondPlace');
                } else if (rank === 3 && participant.status !== 'winner') {
                  rankClass = 'third-place';
                  rankLabel = t('provingGrounds.thirdPlace');
                }
                
                return (
                  <div key={participant.user_id} className={`complete-result-item ${rankClass}`}>
                    <span className="result-rank">{rankLabel}</span>
                    <span className="result-player">{participant.username}</span>
                    <span className="result-status">{participant.status}</span>
                  </div>
                );
              })}
            </div>
            <button
              className="provinggrounds-button"
              onClick={() => {
                playSoundEffect('/sounds/click.mp3');
                setShowCompleteResults(false);
              }}
              style={{ marginTop: '20px' }}
            >
              {t('common.close')}
            </button>
          </div>
        </div>
      )}
        </div>
      </div>
    </div>
  );
}

export default ProvingGrounds;

